import { useEffect, useState } from "react";
import axios from "axios";
import Swal from 'sweetalert2';
import { getCookies } from "cookies-next";
import { config } from "../../data/config";
import { useTranslation } from 'react-i18next';
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";

interface Affiliate {
    id: string;
    _id: string;
    userId: string;
    affiliateCode: string | null;
    status: 'pending' | 'approved' | 'rejected';
    fullName: string;
    email: string;
    gender?: string;
    country?: string;
    audienceRegion?: string;
    socialMediaLink?: string;
    approvedAt?: string;
    rejectedAt?: string;
    createdAt: string;
}

interface PaginatedResponse {
    docs: Affiliate[];
    totalDocs: number;
    limit: number;
    page: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    nextPage: number | null;
    prevPage: number | null;
}

export default function AffiliateRequests() {
    const { t } = useTranslation();

    const breadcrump = [
        { name: t('Dashboard'), url: '/dashboard' },
        { name: t('Affiliate'), url: '/dashboard/Affiliate' },
        { name: t('Affiliate Requests'), url: null },
    ];

    return (
        <div className="bg-gray-50 min-h-screen p-4 md:p-6">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={t('Affiliate Requests')} />
            <div className="mt-6">
                <AffiliateList />
            </div>
        </div>
    );
}

function AffiliateList() {
    const { t } = useTranslation();
    const cookie = getCookies();

    const [list, setList] = useState<PaginatedResponse | null>(null);
    const [name, setName] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [showitem, setShowItem] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const loaddata = async (page = 1) => {
        setLoading(true);
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/affiliate?page=${page}&status=${status}&email=${email}&name=${name}`,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            setList(res.data.message);
            setCurrentPage(page);
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: error.response?.data?.message || t('Error loading data'),
            });
        } finally {
            setLoading(false);
        }
    };

    const approveAffiliate = async (id: string) => {
        Swal.fire({
            title: t('Approve Affiliate'),
            text: t('Are you sure you want to approve this affiliate? They will receive an email with their affiliate code.'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#27ae60',
            cancelButtonColor: '#95a5a6',
            confirmButtonText: t('Yes, Approve'),
            cancelButtonText: t('Cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                setUpdating(id);
                try {
                    await axios({
                        method: 'put',
                        url: `${config.url}/v1/dashboard/affiliate/${id}/approve`,
                        headers: {
                            Authorization: 'Bearer ' + cookie['token'],
                        }
                    });

                    Swal.fire({
                        icon: 'success',
                        title: t('Approved'),
                        text: t('Affiliate approved successfully. Email sent.'),
                        timer: 2000,
                        showConfirmButton: false
                    });

                    loaddata(currentPage);
                } catch (error: any) {
                    Swal.fire({
                        icon: 'error',
                        title: t('Error'),
                        text: error.response?.data?.message || t('Error approving affiliate'),
                    });
                } finally {
                    setUpdating(null);
                }
            }
        });
    };

    const rejectAffiliate = async (id: string) => {
        const { value: reason } = await Swal.fire({
            title: t('Reject Affiliate'),
            input: 'textarea',
            inputLabel: t('Rejection reason (optional)'),
            inputPlaceholder: t('Enter reason for rejection...'),
            showCancelButton: true,
            confirmButtonColor: '#e74c3c',
            cancelButtonColor: '#95a5a6',
            confirmButtonText: t('Reject'),
            cancelButtonText: t('Cancel')
        });

        if (reason !== undefined) {
            setUpdating(id);
            try {
                await axios({
                    method: 'put',
                    url: `${config.url}/v1/dashboard/affiliate/${id}/reject`,
                    data: { reason },
                    headers: {
                        Authorization: 'Bearer ' + cookie['token'],
                    }
                });

                Swal.fire({
                    icon: 'success',
                    title: t('Rejected'),
                    text: t('Affiliate rejected. Email sent.'),
                    timer: 2000,
                    showConfirmButton: false
                });

                loaddata(currentPage);
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: t('Error'),
                    text: error.response?.data?.message || t('Error rejecting affiliate'),
                });
            } finally {
                setUpdating(null);
            }
        }
    };

    const deleteAffiliate = async (id: string) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("Delete this affiliate request!"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: t('Yes, delete it'),
            cancelButtonText: t('Cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios({
                        method: 'delete',
                        url: `${config.url}/v1/dashboard/affiliate/${id}`,
                        headers: {
                            Authorization: 'Bearer ' + cookie['token'],
                        }
                    });

                    Swal.fire({
                        icon: 'success',
                        title: t('Deleted'),
                        text: t('Affiliate deleted successfully'),
                        timer: 1500,
                        showConfirmButton: false
                    });

                    loaddata(currentPage);
                } catch (error: any) {
                    Swal.fire({
                        icon: 'error',
                        title: t('Error'),
                        text: error.response?.data?.message || t('Error deleting affiliate'),
                    });
                }
            }
        });
    };

    function getStatusBadge(status: string) {
        const statusMap: any = {
            'pending': { text: t('Pending'), color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
            'approved': { text: t('Approved'), color: 'bg-green-100 text-green-800 border-green-200' },
            'rejected': { text: t('Rejected'), color: 'bg-red-100 text-red-800 border-red-200' },
        };
        return statusMap[status] || statusMap['pending'];
    }

    function formatDate(isoString: string) {
        const date = new Date(isoString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    useEffect(() => {
        loaddata(1);
    }, []);

    if (loading && !list) {
        return (
            <div className="bg-white rounded-xl shadow p-8">
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
                <p className="text-center mt-4 text-gray-600">{t('Loading...')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Filters Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('Filter & Search')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('Name')}</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            placeholder="Search by name..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('Email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                            placeholder="Search by email..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('Status')}</label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        >
                            <option value="">{t('All Status')}</option>
                            <option value="pending">{t('Pending')}</option>
                            <option value="approved">{t('Approved')}</option>
                            <option value="rejected">{t('Rejected')}</option>
                        </select>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => loaddata(1)}
                            className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            {t('Apply Filter')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            {list && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">{t('Total Requests')}</p>
                                <p className="text-2xl font-bold text-gray-800 mt-1">{list.totalDocs || 0}</p>
                            </div>
                            <div className="bg-blue-100 p-3 rounded-lg">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">{t('Pending')}</p>
                                <p className="text-2xl font-bold text-gray-800 mt-1">
                                    {list.docs?.filter((item) => item.status === 'pending').length || 0}
                                </p>
                            </div>
                            <div className="bg-yellow-100 p-3 rounded-lg">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">{t('Approved')}</p>
                                <p className="text-2xl font-bold text-gray-800 mt-1">
                                    {list.docs?.filter((item) => item.status === 'approved').length || 0}
                                </p>
                            </div>
                            <div className="bg-green-100 p-3 rounded-lg">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">{t('Rejected')}</p>
                                <p className="text-2xl font-bold text-gray-800 mt-1">
                                    {list.docs?.filter((item) => item.status === 'rejected').length || 0}
                                </p>
                            </div>
                            <div className="bg-red-100 p-3 rounded-lg">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Requests List */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    {t('Affiliate')}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    {t('Contact')}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    {t('Code')}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    {t('Status')}
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    {t('Date')}
                                </th>
                                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    {t('Actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {list && list.docs && list.docs.length > 0 ? (
                                list.docs.map((item, index) => {
                                    const statusBadge = getStatusBadge(item.status);
                                    const isExpanded = showitem === index;

                                    return (
                                        <>
                                            <tr key={item.id || item._id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {item.fullName}
                                                    </div>
                                                    {item.country && (
                                                        <div className="text-sm text-gray-500">{item.country}</div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        <a href={`mailto:${item.email}`} className="text-blue-600 hover:text-blue-800">
                                                            {item.email}
                                                        </a>
                                                    </div>
                                                    {item.socialMediaLink && (
                                                        <div className="text-sm text-gray-500">
                                                            <a href={item.socialMediaLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                                {t('Social Media')} ↗
                                                            </a>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {item.affiliateCode ? (
                                                        <span className="px-3 py-1 bg-purple-100 text-purple-800 font-mono font-bold rounded-lg">
                                                            {item.affiliateCode}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-3 py-1.5 text-sm font-medium rounded-full border ${statusBadge.color}`}>
                                                        {statusBadge.text}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(item.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <button
                                                            onClick={() => setShowItem(isExpanded ? null : index)}
                                                            className="p-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                                                            title="View details"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isExpanded ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                                                            </svg>
                                                        </button>
                                                        {item.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => approveAffiliate(item.id || item._id)}
                                                                    disabled={updating === (item.id || item._id)}
                                                                    className="p-2 text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                                                                    title="Approve"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                </button>
                                                                <button
                                                                    onClick={() => rejectAffiliate(item.id || item._id)}
                                                                    disabled={updating === (item.id || item._id)}
                                                                    className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                                                                    title="Reject"
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                    </svg>
                                                                </button>
                                                            </>
                                                        )}
                                                        <button
                                                            onClick={() => deleteAffiliate(item.id || item._id)}
                                                            className="p-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="bg-gray-50">
                                                    <td colSpan={6} className="px-6 py-6">
                                                        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                                                            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                </svg>
                                                                {t('Affiliate Details')}
                                                            </h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('Full Name')}</label>
                                                                    <p className="text-gray-900">{item.fullName}</p>
                                                                </div>
                                                                <div>
                                                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('Email')}</label>
                                                                    <p className="text-gray-900">{item.email}</p>
                                                                </div>
                                                                {item.gender && (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Gender')}</label>
                                                                        <p className="text-gray-900">{item.gender}</p>
                                                                    </div>
                                                                )}
                                                                {item.country && (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Country')}</label>
                                                                        <p className="text-gray-900">{item.country}</p>
                                                                    </div>
                                                                )}
                                                                {item.audienceRegion && (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Audience Region')}</label>
                                                                        <p className="text-gray-900">{item.audienceRegion}</p>
                                                                    </div>
                                                                )}
                                                                {item.socialMediaLink && (
                                                                    <div className="md:col-span-3">
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Social Media Link')}</label>
                                                                        <a href={item.socialMediaLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                                            {item.socialMediaLink}
                                                                        </a>
                                                                    </div>
                                                                )}
                                                                {item.affiliateCode && (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Affiliate Code')}</label>
                                                                        <span className="px-4 py-2 bg-purple-100 text-purple-800 font-mono font-bold text-lg rounded-lg inline-block">
                                                                            {item.affiliateCode}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {item.approvedAt && (
                                                                    <div>
                                                                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('Approved At')}</label>
                                                                        <p className="text-gray-900">{formatDate(item.approvedAt)}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center">
                                            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <p className="text-gray-500 text-lg">{t('No affiliate requests found')}</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {list && list.totalPages > 1 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-700">
                                {t('Showing')} {((list.page - 1) * list.limit) + 1} {t('to')} {Math.min(list.page * list.limit, list.totalDocs)} {t('of')} {list.totalDocs} {t('requests')}
                            </div>
                            <nav className="flex items-center gap-2">
                                {list.prevPage && (
                                    <button
                                        onClick={() => loaddata(list.page - 1)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {t('Previous')}
                                    </button>
                                )}
                                {Array.from({ length: Math.min(list.totalPages, 5) }, (_, index) => {
                                    const pageNum = index + 1;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => loaddata(pageNum)}
                                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${list.page === pageNum
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                                {list.nextPage && (
                                    <button
                                        onClick={() => loaddata(list.page + 1)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        {t('Next')}
                                    </button>
                                )}
                            </nav>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
